import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { convertGraphqlBaseTestItemToDto, convertGraphqlFormFieldToDto, convertGraphqlResponseToDto, convertGraphqlResponseToDtoSimpleReturnValue } from 'src/app/graphQL/mappers/common.mappers';
import { convertSpecificationCoverageDtoToGraphqlInput } from 'src/app/graphQL/mappers/specification-coverage.mappers';
import { GraphQLBaseTestItemOutput } from 'src/app/graphQL/models/output/graphql-base-test-item-output';
import { GraphQLFormFieldOutput } from 'src/app/graphQL/models/output/graphql-form-field-output';
import { GraphQLResponseOutput } from 'src/app/graphQL/models/output/graphql-response-output';
import { CREATE_SPECIFICATION_COVERAGE_MUTATION, DELETE_SPECIFICATION_COVERAGE_MUTATION, UPDATE_SPECIFICATION_COVERAGE_MUTATION } from 'src/app/graphQL/mutations/specification-coverage-mutations';
import { GETALL_SPECIFICATION_COVERAGE_QUERY, GET_SPECIFICATION_COVERAGE_SEARCH_UI_TEMPLATE_QUERY, GET_SPECIFICATION_COVERAGE_QUERY, GET_SPECIFICATION_COVERAGE_UI_TEMPLATE_QUERY, SEARCH_SPECIFICATION_COVERAGE_QUERY } from 'src/app/graphQL/queries/specification-coverage-queries';
import { BaseTestItemDto } from 'src/app/models/dto/Base-Test-item-dto';
import { ResponseDto } from 'src/app/models/dto/responseDto';
import { SpecificationCoverageDto } from 'src/app/models/dto/specificationCoverageDto';
import { formField } from 'src/app/models/formField';
import { ApolloClientService } from './apollo-client.service';
import { DualApiService, Scope } from '../migration/dual-api.service';

@Injectable({
  providedIn: 'root',
})
export class SpecificationCoverageApiService {

  constructor(
    private apolloClientService: ApolloClientService,
    private dualApiService: DualApiService
  ) {}

  /**
   * `regBy` is the old API's "only mine" switch, which maps onto the Go lambda's
   * `scope: MINE` (resolved server-side from the authorizer, so the client cannot ask for
   * someone else's items). Nothing maps to MY_GROUP: no specification coverage item
   * carries functionalGroupOwners — the old SpecificationCoverageInput has no OwnerIds —
   * so that scope would always return an empty page.
   */
  getAll(regBy: string): Observable<BaseTestItemDto[]> {
    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorAllSpecificationCoverages : GraphQLBaseTestItemOutput[]}>({
          query: GETALL_SPECIFICATION_COVERAGE_QUERY,
          variables: {RegBy: regBy}
        })
      ),
      map(results => {
        const specCoverages = results.data.configuratorAllSpecificationCoverages
        if (!specCoverages)
          return undefined

        return specCoverages.map(item => {
          return convertGraphqlBaseTestItemToDto(item);
        })
      })
    );

    const scope: Scope | undefined = regBy ? 'MINE' : undefined;

    return this.dualApiService.dualGetAll('specificationCoverage', [], oldApiCall$, scope);
  }

  getByUid(uid: string): Observable<ResponseDto> {
    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorSpecificationCoverageById: GraphQLResponseOutput}>({
          query: GET_SPECIFICATION_COVERAGE_QUERY,
          variables: {Uid: uid}
        })
      ),
      map(results => {
        const specCoverage = results.data.configuratorSpecificationCoverageById
        if (!specCoverage)
          return undefined

        return convertGraphqlResponseToDto<SpecificationCoverageDto>(specCoverage);
      })
    );

    return this.dualApiService.dualGetByUid('specificationCoverage', uid, oldApiCall$);
  }

  // UI templates stay on the old API, as for every other entity.
  getUiTemplate(): Observable<formField[]> {
    return this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorSpecificationCoverageUiTemplate: GraphQLFormFieldOutput[]}>({
          query: GET_SPECIFICATION_COVERAGE_UI_TEMPLATE_QUERY,
          variables: {}
        })
      ),
      map(results => {
        const specCoverageUiTemplate = results.data.configuratorSpecificationCoverageUiTemplate
        if (!specCoverageUiTemplate)
          return undefined

        return specCoverageUiTemplate.map(item => {
          return convertGraphqlFormFieldToDto(item);
        })
      })
    )
  }

  getSearchUiTemplate(): Observable<formField[]> {
    return this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorSpecificationCoverageSearchUiTemplate: GraphQLFormFieldOutput[]}>({
          query: GET_SPECIFICATION_COVERAGE_SEARCH_UI_TEMPLATE_QUERY,
          variables: {}
        })
      ),
      map(results => {
        const specCoverageUiTemplate = results.data.configuratorSpecificationCoverageSearchUiTemplate

        if (!specCoverageUiTemplate)
          return undefined

        return specCoverageUiTemplate.map(item => {
          return convertGraphqlFormFieldToDto(item);
        })
      })
    )
  }

  create(specificationCoverage: any): Observable<ResponseDto> {
    const graphqlSpecCoverageInput = convertSpecificationCoverageDtoToGraphqlInput(specificationCoverage)

    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.mutate<{configuratorCreateSpecificationCoverage: GraphQLResponseOutput}>({
          mutation: CREATE_SPECIFICATION_COVERAGE_MUTATION,
          variables: {SpecificationCoverage: graphqlSpecCoverageInput }
        })
      ),
      map(result => {
        const createdSpecCoverage = result.data.configuratorCreateSpecificationCoverage
        if (!createdSpecCoverage)
          return undefined

        return convertGraphqlResponseToDtoSimpleReturnValue(createdSpecCoverage as GraphQLResponseOutput)
      })
    );

    // The DTO, not the PascalCase GraphQL input: GoLambdaMapperService does its own
    // camelCase conversion and Go-schema cleaning.
    return this.dualApiService.dualCreate('specificationCoverage', specificationCoverage, oldApiCall$);
  }

  update(specificationCoverage: any): Observable<ResponseDto> {
    const graphqlSpecCoverageInput = convertSpecificationCoverageDtoToGraphqlInput(specificationCoverage)

    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.mutate<{configuratorUpdateSpecificationCoverage: GraphQLResponseOutput}>({
          mutation: UPDATE_SPECIFICATION_COVERAGE_MUTATION,
          variables: {SpecificationCoverage: graphqlSpecCoverageInput }
        })
      ),
      map(result =>{
        const updatedSpecCoverage = result.data.configuratorUpdateSpecificationCoverage
        if (!updatedSpecCoverage)
          return undefined

        return convertGraphqlResponseToDtoSimpleReturnValue(result.data.configuratorUpdateSpecificationCoverage as GraphQLResponseOutput)
      } )
    );

    // dualUpdate strips uid out of the payload — the Go mutation takes it as its own
    // argument and rejects it inside data.
    return this.dualApiService.dualUpdate('specificationCoverage', specificationCoverage.uid, specificationCoverage, oldApiCall$);
  }

  /** `regBy` is unused by both APIs — each identifies the caller from its own auth context. */
  delete(uid: string, regBy: string): Observable<any> {
    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.mutate({
          mutation: DELETE_SPECIFICATION_COVERAGE_MUTATION,
          variables: {Uid: uid}
        })
      ),
      map(result =>(result.data))
    );

    return this.dualApiService.dualDelete('specificationCoverage', uid, oldApiCall$);
  }

  search(specificationCoverage: SpecificationCoverageDto, scope?: Scope): Observable<BaseTestItemDto[]> {
    const graphqlSpecCoverageInput = convertSpecificationCoverageDtoToGraphqlInput(specificationCoverage);

    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorSearchSpecificationCoverage: GraphQLBaseTestItemOutput[]}>({
          query: SEARCH_SPECIFICATION_COVERAGE_QUERY,
          variables: {SpecificationCoverage: graphqlSpecCoverageInput }
        })
      ),
      map(results => {
        const specCoverages = results?.data?.configuratorSearchSpecificationCoverage
        if (!specCoverages)
          return undefined;

        return specCoverages.map(item => {
          return convertGraphqlBaseTestItemToDto(item);
        })
      })
    );

    return this.dualApiService.dualSearch('specificationCoverage', specificationCoverage, oldApiCall$, scope);
  }
}
